if (!customElements.get('cart-terms')) {
  class CartTerms extends HTMLElement {
    constructor() {
      super();

      this.cartTermsCheckbox = this.querySelector('.js-cart-terms-checkbox');
      if (!this.cartTermsCheckbox) return;

      this.formId = this.cartTermsCheckbox.getAttribute('form');
      this.form = this.formId ? document.getElementById(this.formId) : this.cartTermsCheckbox.closest('form');
      if (!this.form) return;

      this.errorElement = this.querySelector('.js-cart-terms-error');

      this.submitHandler = this.handleSubmit.bind(this);
      this.changeHandler = this.handleChange.bind(this);
      this.checkoutClickHandler = this.handleCheckoutClick.bind(this);

      // Capture phase is deliberate:
      // it blocks checkout before theme/app bubble listeners can act.
      this.form.addEventListener('submit', this.submitHandler, true);
      this.cartTermsCheckbox.addEventListener('change', this.changeHandler);

      this.checkoutButtons = this.getCheckoutButtons();
      this.checkoutButtons.forEach((button) => {
        button.addEventListener('click', this.checkoutClickHandler, true);
      });
    }

    disconnectedCallback() {
      if (this.form) {
        this.form.removeEventListener('submit', this.submitHandler, true);
      }

      if (this.cartTermsCheckbox) {
        this.cartTermsCheckbox.removeEventListener('change', this.changeHandler);
      }

      if (this.checkoutButtons) {
        this.checkoutButtons.forEach((button) => {
          button.removeEventListener('click', this.checkoutClickHandler, true);
        });
      }
    }

    /**
     * Finds every checkout submitter associated with this form.
     * Handles both:
     * - cart drawer: checkout button outside form using form="cart-drawer-form"
     * - cart page: checkout button inside the form
     * @returns {HTMLElement[]}
     */
    getCheckoutButtons() {
      const buttons = new Set(
        Array.from(this.form.querySelectorAll('button[name="checkout"], input[name="checkout"]'))
      );

      if (this.form.id) {
        document
          .querySelectorAll(`button[name="checkout"][form="${CSS.escape(this.form.id)}"], input[name="checkout"][form="${CSS.escape(this.form.id)}"]`)
          .forEach((button) => buttons.add(button));
      }

      return Array.from(buttons);
    }

    /**
     * Hard-blocks the actual checkout click before the browser/form or
     * another theme listener can continue.
     * @param {MouseEvent} evt
     */
    handleCheckoutClick(evt) {
      if (this.cartTermsCheckbox.checked) {
        this.hideError();
        return;
      }

      evt.preventDefault();
      evt.stopPropagation();
      evt.stopImmediatePropagation();
      this.showError();
    }

    /**
     * Fallback protection for keyboard submit, requestSubmit(), or another
     * script attempting to submit the cart form.
     * @param {SubmitEvent} evt
     */
    handleSubmit(evt) {
      if (this.cartTermsCheckbox.checked) {
        this.hideError();
        return;
      }

      const submitter = evt.submitter;

      // Only block checkout-style submissions when a submitter is known.
      if (submitter && submitter.name && submitter.name !== 'checkout') {
        return;
      }

      evt.preventDefault();
      evt.stopPropagation();
      evt.stopImmediatePropagation();
      this.showError();
    }

    handleChange() {
      if (this.cartTermsCheckbox.checked) {
        this.hideError();
      }
    }

    showError() {
      this.cartTermsCheckbox.setAttribute('aria-invalid', 'true');

      if (this.errorElement) {
        this.errorElement.hidden = false;

        if (this.errorElement.id) {
          this.cartTermsCheckbox.setAttribute('aria-describedby', this.errorElement.id);
        }
      }

      try {
        this.cartTermsCheckbox.focus({ preventScroll: true });
      } catch (error) {
        this.cartTermsCheckbox.focus();
      }

      this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    hideError() {
      this.cartTermsCheckbox.removeAttribute('aria-invalid');
      this.cartTermsCheckbox.removeAttribute('aria-describedby');

      if (this.errorElement) {
        this.errorElement.hidden = true;
      }
    }
  }

  customElements.define('cart-terms', CartTerms);
}
